// Iter-158.13.2.20f.33 — AllergyBadge.
//
// Small, deliberately conspicuous safety indicator surfaced
// wherever an H&M user is working on a Character that has a
// populated Character-level allergy note. Renders NOTHING when
// no allergy exists — no fallback placeholder text of any kind
// (per Alex's spec: absent when empty, not narrated).
//
// Click behaviour: navigates to the Character detail page with
// `#allergy` hash, which CharacterDetail scrolls to reveal the
// source note text. Consumers pass the navigation target so
// this component stays presentation-only.
//
// Reused by:
//   - CharacterDetail character-header (own page — anchor scroll
//     only; no cross-page navigation)
//   - ContinuityBlock character sub-header (Scene view)
//   - BetaHMCharacters Cast picker rows
//
// Kept ~30 lines. No business logic. No fetching. The caller
// decides visibility (pass `note={null}` to hide).
import { AlertOctagon } from "lucide-react";
import { Link } from "react-router-dom";

export default function AllergyBadge({ note, to, onClick, testid = "allergy-badge" }) {
    if (!note) return null;

    const content = (
        <>
            <AlertOctagon size={11} className="shrink-0" aria-hidden="true" />
            <span className="whitespace-nowrap">Allergy</span>
        </>
    );

    const base =
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] " +
        "uppercase tracking-[0.15em] font-semibold bg-red-500/15 text-red-200 " +
        "border-red-400/30 hover:bg-red-500/25 hover:text-red-100 transition-colors " +
        "whitespace-nowrap";

    if (to) {
        return (
            <Link
                to={to}
                data-testid={testid}
                title={note.text ? String(note.text).slice(0, 200) : "Allergy note"}
                onClick={onClick}
                className={base}
            >
                {content}
            </Link>
        );
    }

    return (
        <button
            type="button"
            data-testid={testid}
            title={note.text ? String(note.text).slice(0, 200) : "Allergy note"}
            onClick={onClick}
            className={base}
        >
            {content}
        </button>
    );
}

// Iter-158.13.2.20f.33 — canonical predicate reused across
// consumers (CharacterDetail scoped list, SceneDetail project
// map, BetaHMCharacters row map) so the "allergy note"
// definition lives in exactly one place.
//
//   - Character-level: `scene_id` is null / undefined.
//   - Tagged with the canonical "allergy" tag.
//   - Not archived (defensive; supervisor_notes archive is
//     server-side but readers should mirror).
//
// Returns the note itself (truthy) or null.
export function pickCharacterAllergyNote(notes, characterId) {
    if (!Array.isArray(notes) || !characterId) return null;
    for (const n of notes) {
        if (!n || n.archived === true) continue;
        if (n.character_id !== characterId) continue;
        if (n.scene_id) continue;
        const tags = Array.isArray(n.tags) ? n.tags : [];
        if (tags.includes("allergy")) return n;
    }
    return null;
}
